
import {
  defineComponent,
  defineAsyncComponent,
  ref,
  onMounted,
  computed,
} from 'vue';

import { useI18n } from 'vue-i18n';

import numeral from 'numeral';

import type {
  Option,
  Projects,
  Statistics,
} from '@/types';

import { TRAFFIC_SOURCES } from '@/libs/consts';

import useUser from '@/composable/useUser';

import useEntriesNext from '@/composable/useEntries@next';

import useUrlState from '@/composable/useUrlState';

export default defineComponent({
  components: {
    User: defineAsyncComponent(() => import('@/components/User.vue')),
    Endpoint: defineAsyncComponent(() => import('@/components/Endpoint.vue')),
    Chartist: defineAsyncComponent(() => import('@/components/Chartist.vue')),
  },
  setup() {
    const i18n = useI18n();

    const { activeUser: user } = useUser();

    const {
      entriesLoading: projectsLoading,
      entries: projects,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects');

    const {
      entriesLoading: statisticsLoading,
      entries: statistics,
      fetchEntries: fetchStatistics,
    } = useEntriesNext<Statistics>('/api/statistics/getStatisticsByEndpointsActivity');

    const statisticsFilters = useUrlState('filters', {
      interval_hours: 24,
      project_id: [],
      traffic_source: null,
      publisher_id: [],
      endpoint_id: [],
    });

    const usersParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? statisticsFilters.project_id.join(',') : undefined,
    }));

    const endpointsParams = computed(() => ({
      include: 'project,user',
      'filter[project_id]': user.isSuperAdmin() ? statisticsFilters.project_id.join(',') : undefined,
      'filter[user_id]': statisticsFilters.publisher_id.join(','),
    }));

    const showStatisticsFilters = ref<boolean>(false);

    const fetchStatisticsWrapper = async (): Promise<void> => fetchStatistics(statisticsFilters);

    const onUserChange = (): void => {
      statisticsFilters.endpoint_id = [];
    };

    const onProjectChange = (): void => {
      statisticsFilters.publisher_id = [];

      onUserChange();
    };

    const intervalHoursOptions: Option[] = [3, 6, 12, 24, 48, 72, 168, 336].map((value: number) => {
      const labelMap: { [key: number]: string; } = {
        48: '2 days',
        72: '3 days',
        168: '1 week',
        336: '2 weeks',
      };

      return {
        value,
        label: `${labelMap[value] || value}`,
      };
    });

    const axisYLabelInterpolationFunction = (value: number): string => numeral(value).format('0a');

    const axisYLabelInterpolationFunctionEstimatedProfit = (value: number): string => i18n.n(value, 'payout');

    onMounted((): void => {
      Promise.all([
        user.isSuperAdmin() ? fetchProjects() : Promise.resolve(),
        fetchStatisticsWrapper(),
      ]);
    });

    return {
      TRAFFIC_SOURCES,
      user,
      projectsLoading,
      projects,
      statisticsLoading,
      statistics,
      fetchStatisticsWrapper,
      statisticsFilters,
      showStatisticsFilters,
      usersParams,
      endpointsParams,
      onUserChange,
      onProjectChange,
      intervalHoursOptions,
      axisYLabelInterpolationFunction,
      axisYLabelInterpolationFunctionEstimatedProfit,
    };
  },
});
